import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Toolbar } from '@mui/material';
import Header from './Header';
import Sidebar from './Sidebar';
import ErrorBoundary from '../ErrorBoundary';

const drawerWidth = 240;

/** Routes that use full-height flex + inner table scroll (see tablePageLayout.js on pages). */
const PINNED_SCROLL_LAYOUT_PATHS = new Set([
  '/horses',
  '/breeding',
  '/mare-owners',
  '/appointments',
  '/vets',
  '/trainers',
  '/feed',
  '/inventory',
  '/expenses',
  '/income',
  '/shows',
  '/time-clock',
  '/admin/users',
  '/admin/expense-types',
  '/admin/expense-locations',
  '/admin/income-types',
  '/admin/income-locations',
  '/admin/horse-pedigrees',
  '/admin/useful-links',
]);

const isPinnedScrollLayoutRoute = (pathname) => {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized.startsWith('/horses/') && normalized !== '/horses') {
    return false;
  }
  return PINNED_SCROLL_LAYOUT_PATHS.has(normalized);
};

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const usePinnedScrollLayout = isPinnedScrollLayoutRoute(location.pathname);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Header onMenuClick={handleDrawerToggle} />
      <Sidebar
        drawerWidth={drawerWidth}
        mobileOpen={mobileOpen}
        onDrawerToggle={handleDrawerToggle}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          height: usePinnedScrollLayout ? '100vh' : 'auto',
          minHeight: '100vh',
          overflow: usePinnedScrollLayout ? 'hidden' : 'visible',
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: 'hidden',
            overflowY: usePinnedScrollLayout ? 'hidden' : 'auto',
          }}
        >
          {/* Keyed on the path so navigating away clears a caught error: a boundary
              holds its failed state forever otherwise, and the next page would render
              the error card instead of itself. */}
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;

